import { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

const SUPPORT_EMAIL = "info@inquira.online";

type ChatMsg = { role: "user" | "system"; text: string; at: number };

/**
 * Lightweight in-app live chat. Messages are persisted as `feedback` rows of
 * type "chat" so the admin support inbox sees the full thread, and the
 * user can also hand the transcript off to info@inquira.online.
 */
export function LiveChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<ChatMsg[]>([
    {
      role: "system",
      text: `Hi! You're chatting with Inquira support. A human replies from ${SUPPORT_EMAIL}, usually within 1–2 business days. Send your first message below.`,
      at: Date.now(),
    },
  ]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [msgs, open]);

  async function send() {
    if (!user) {
      toast.error("Sign in to start a chat");
      return;
    }
    const body = text.trim();
    if (!body) return;
    setSending(true);
    const tid = threadId ?? crypto.randomUUID();
    const { error } = await supabase.from("feedback").insert({
      user_id: user.id,
      type: "chat",
      message: `[thread:${tid}]\n${body}`.slice(0, 4000),
      page_url: window.location.pathname,
    });
    setSending(false);
    if (error) {
      toast.error("Could not send message");
      return;
    }
    setThreadId(tid);
    setMsgs((m) => [
      ...m,
      { role: "user", text: body, at: Date.now() },
      {
        role: "system",
        text: `Message received. We'll reply to ${user.email ?? "your account email"} from ${SUPPORT_EMAIL}.`,
        at: Date.now(),
      },
    ]);
    setText("");
  }

  function emailTranscript() {
    const transcript = msgs
      .filter((m) => m.role === "user")
      .map((m) => `- ${new Date(m.at).toLocaleString()}: ${m.text}`)
      .join("\n");
    const href = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
      "Inquira live chat transcript",
    )}&body=${encodeURIComponent(transcript || "(empty)")}`;
    window.location.href = href;
  }

  return (
    <>
      <button
        aria-label="Open live chat"
        onClick={() => setOpen(true)}
        className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] right-4 z-40 flex h-12 items-center gap-2 rounded-full bg-primary px-4 text-primary-foreground shadow-lg hover:opacity-90"
      >
        <MessageCircle className="h-5 w-5" /> Chat
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-end bg-black/40 safe-x sm:p-4">
          <div className="safe-bottom flex h-[85dvh] max-h-[85dvh] w-full max-w-md flex-col rounded-t-2xl bg-card shadow-xl sm:h-[560px] sm:rounded-2xl">
            <div className="flex items-center justify-between border-b border-border p-3">
              <div>
                <div className="text-sm font-semibold">Inquira Support</div>
                <div className="text-[11px] text-muted-foreground">
                  Replies from {SUPPORT_EMAIL}
                </div>
              </div>
              <button onClick={() => setOpen(false)} aria-label="Close chat">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto p-3">
              {msgs.map((m, i) => (
                <div
                  key={i}
                  className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                    m.role === "user"
                      ? "ml-auto bg-primary text-primary-foreground"
                      : "bg-muted text-foreground"
                  }`}
                >
                  {m.text}
                </div>
              ))}
            </div>
            <div className="border-t border-border p-3">
              <div className="flex items-end gap-2">
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void send();
                    }
                  }}
                  rows={2}
                  maxLength={4000}
                  placeholder={user ? "Type a message…" : "Sign in to chat"}
                  disabled={!user || sending}
                  className="flex-1 resize-none rounded-lg border border-border bg-background p-2 text-sm"
                />
                <button
                  onClick={send}
                  disabled={!user || sending || !text.trim()}
                  className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground disabled:opacity-50"
                  aria-label="Send message"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
              <button
                onClick={emailTranscript}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-border py-1.5 text-xs text-muted-foreground hover:bg-muted/50"
              >
                <Mail className="h-3.5 w-3.5" /> Email transcript to {SUPPORT_EMAIL}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}